// AupPage — UAVSimNet Acceptable Use Policy.
// Issued by The RLK Group (Scottish limited company, SC781391).
// Governing law: Scotland. Counter-UAS SaaS, dual-use analytic software.

import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const EFFECTIVE_DATE = "25 April 2026";

export default function AupPage() {
    return (
        <>
            <SeoHead
                path="/aup"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/aup", label: "Acceptable Use Policy" }),
                ]}
            />
        <div data-testid="aup-page">
            <section className="section" style={{ paddingTop: 64 }}>
                <div className="section__inner legal-doc">
                    <span className="section__eyebrow">// LEGAL · ACCEPTABLE USE</span>
                    <h1 className="section__heading">Acceptable Use Policy</h1>
                    <div
                        className="legal-doc__banner"
                        data-testid="aup-issued-banner"
                    >
                        Issued by The RLK Group · Company No. SC781391 · Effective from {EFFECTIVE_DATE}
                    </div>

                    <h2>1. Scope</h2>
                    <p>
                        This Acceptable Use Policy (the "<strong>AUP</strong>") is
                        issued by <strong>The RLK Group</strong> (a Scottish
                        registered company, <strong>SC781391</strong>) and governs
                        all use of UAVSimNet (the "<strong>Platform</strong>") by
                        customers, their Authorised Users, and any third party
                        permitted to access the Platform through a customer tenant.
                        It is incorporated by reference into our Terms of Service
                        (<Link to="/terms">/terms</Link>) and forms part of the
                        contract between the customer and RLK. Breach of this AUP is
                        a material breach of the Terms of Service and may result in
                        suspension or termination of access under the provisions of
                        those Terms.
                    </p>

                    <h2>2. Eligibility</h2>
                    <p>
                        Access to the Platform is restricted to organisations
                        operating in the defence, government, law-enforcement, or
                        licensed-industry sectors, and to academic research teams
                        conducting work under appropriate ethical and export-control
                        oversight. At access-request time, each applicant
                        attests to the organisation they represent, their role
                        within that organisation, and their intended use case. Those
                        representations must remain true for the duration of the
                        customer's engagement with RLK; if any representation ceases
                        to be true, the customer must notify us in writing before
                        continuing to use the Platform.
                    </p>

                    <h2>3. Permitted use</h2>
                    <p>The Platform is provided for the following purposes:</p>
                    <ul>
                        <li>
                            Counter-UAS engagement simulation, mission analysis, and
                            trade studies against authorised threat profiles.
                        </li>
                        <li>
                            Interceptor design evaluation, including statistical
                            analysis of candidate platforms, guidance approaches,
                            and sensor configurations.
                        </li>
                        <li>
                            Training and mission rehearsal for authorised operators,
                            where the Platform is used as an analytic environment
                            rather than as a real-time fire-control system.
                        </li>
                        <li>
                            Academic research, where the host institution has an
                            approved research plan and, where applicable, an ethics
                            committee review and export-control determination.
                        </li>
                    </ul>
                    <p>
                        All permitted use must be consistent with applicable
                        export-control laws — including, as relevant to the customer,
                        UK Strategic Export Controls, EU dual-use controls, US ITAR
                        and EAR, and equivalent national regimes.
                    </p>

                    <h2>4. Prohibited use</h2>
                    <p>You must not use the Platform, or allow it to be used, to:</p>
                    <ul>
                        <li>
                            Plan, direct, or facilitate the unlawful targeting of
                            civilians, civilian aviation, civilian infrastructure, or
                            any other target protected by international humanitarian
                            law, the law of armed conflict, or applicable domestic
                            law.
                        </li>
                        <li>
                            Support the unlawful weaponisation of unmanned or
                            autonomous systems, or to circumvent obligations under
                            applicable arms-control regimes.
                        </li>
                        <li>
                            Evade or attempt to evade sanctions administered by the
                            United Nations, the United Kingdom, the European Union,
                            the United States (including measures administered by
                            OFAC), or any other competent authority.
                        </li>
                        <li>
                            Provide access to the Platform, or the outputs of the
                            Platform, to any person or entity on a prohibited-persons
                            or sanctioned-entity list, or to any person or entity
                            located in a comprehensively sanctioned jurisdiction.
                        </li>
                        <li>
                            Reverse engineer, decompile, disassemble, or otherwise
                            attempt to derive the source code, model weights,
                            training-data content, or non-public interfaces of the
                            Platform, except as expressly permitted by mandatory
                            applicable law.
                        </li>
                        <li>
                            Share credentials, transfer access between individuals,
                            create concealed or undisclosed seats, or otherwise
                            circumvent the per-user authentication model.
                        </li>
                        <li>
                            Submit to the Platform third-party personal data,
                            controlled technical data, classified information, or any
                            other data that the customer is not authorised to process
                            or transfer to RLK's infrastructure.
                        </li>
                        <li>
                            Attempt to access data belonging to another customer
                            tenant; attempt to bypass tenant isolation, authentication,
                            session expiry, or rate limits; probe the Platform for
                            vulnerabilities other than under a written coordinated-
                            disclosure agreement with RLK; or conduct unauthorised
                            penetration testing or load testing.
                        </li>
                        <li>
                            Upload malware, executable payloads, or content that
                            could damage, disable, or overburden the Platform or any
                            other customer's use of it.
                        </li>
                        <li>
                            Perform bulk scraping of the public Site, public
                            documentation, or any other pages intended for ordinary
                            human consumption, whether by automated means or
                            otherwise.
                        </li>
                    </ul>

                    <h2>5. Reporting and cooperation</h2>
                    <p>
                        Customers must promptly report to RLK any actual or suspected
                        security incident, unauthorised access, credential
                        compromise, or material breach of this AUP by themselves,
                        their Authorised Users, or any third party. Reports should
                        be sent in writing to The RLK Group at its registered
                        office: Unit 4 Strathclyde Business Centre, Cambuslang,
                        United Kingdom, G72 7XR, within seventy-two (72) hours of
                        becoming aware of the incident. Customers must cooperate in
                        good faith with any investigation, preservation request, or
                        other lawful request made by RLK or by a competent authority
                        relating to the Platform or its use. Customers must not take
                        any action that could obstruct such an investigation.
                    </p>

                    <h2>6. Suspension and termination</h2>
                    <p>
                        Where RLK reasonably suspects a breach of this AUP, we may
                        suspend access to the Platform immediately, in whole or in
                        part, pending investigation. We will give the customer
                        notice of the suspension where it is practicable and lawful
                        to do so; in urgent circumstances — including any case where
                        continued access could cause material harm to the Platform,
                        to other customers, or to the public interest — we may
                        suspend immediately and give notice as soon as reasonably
                        practicable thereafter.
                    </p>
                    <p>
                        Material or repeated breach of this AUP is a ground for
                        termination of the Terms of Service under §13 of those
                        Terms (Term and termination) and §14 (Suspension). Breach
                        of §4 of this AUP, the prohibited-use clause, is treated by
                        RLK as a material breach in every case.
                    </p>

                    <h2>7. Survival</h2>
                    <p>
                        The obligations on the customer relating to confidentiality,
                        intellectual property, indemnity, export-control compliance,
                        and sanctions representations survive any suspension or
                        termination of access to the Platform. Where RLK has
                        preserved evidence of AUP breach at the time of suspension,
                        RLK may continue to hold that evidence for the periods set
                        out in our Privacy Notice (<Link to="/privacy">/privacy</Link>)
                        for the purposes of investigation, dispute resolution, and
                        regulatory cooperation.
                    </p>

                    <h2>8. Updates</h2>
                    <p>
                        We may update this AUP from time to time. The current
                        version is always available at{" "}
                        <Link to="/aup">/aup</Link>, with the effective date shown in
                        the banner at the top. Where a change is material, we will
                        notify active customers by email at least thirty (30) days
                        before the change takes effect. Continued use of the
                        Platform after the effective date constitutes acceptance of
                        the updated AUP.
                    </p>

                    <h2>9. Governing law and contact</h2>
                    <p>
                        This AUP is governed by Scottish law and the parties submit
                        to the exclusive jurisdiction of the Court of Session,
                        Edinburgh, Scotland, consistent with §18 of the Terms of
                        Service. Questions relating to this AUP should be sent in
                        writing to The RLK Group at its registered office:
                    </p>
                    <p>
                        <strong>The RLK Group</strong> (Company No. SC781391)
                        <br />
                        Unit 4 Strathclyde Business Centre
                        <br />
                        Cambuslang
                        <br />
                        United Kingdom
                        <br />
                        G72 7XR
                    </p>

                    <div
                        className="legal-doc__crosslinks"
                        data-testid="aup-crosslinks"
                    >
                        <span>See also:</span>
                        <Link to="/terms">Terms of Service</Link>
                        <span>·</span>
                        <Link to="/privacy">Privacy Notice</Link>
                    </div>
                    <div className="legal-doc__updated">Last updated: {EFFECTIVE_DATE}</div>
                </div>
            </section>
        </div>
        </>
    );
}
